<script setup>
import { ref, computed } from 'vue'
import axios from '@/axios.js'
import { useNotificationStore } from '@/stores/notificationStore'
import { useUserStore } from '@/stores/userStore'

const userStore = useUserStore()
const notificationStore = useNotificationStore()

const currentUser = computed(() => userStore.currentUser || {
  name: 'Guest',
  email: '',
  avatar: null,
  role: '',
  status: 'offline'
})

const showConfirmModal = ref(false)
const isSyncing = ref(false)

const openConfirmModal = () => {
  showConfirmModal.value = true
}

const closeConfirmModal = () => {
  showConfirmModal.value = false
}

const triggerBoxscoreSync = () => {
  isSyncing.value = true
  closeConfirmModal()
  
  axios.get('/api/sync/boxscore')
    .then(response => {
      console.log('Boxscore sync response:', response.data)
      
      // Show success notification
      if (response.data.success) {
        notificationStore.addNotification({
          type: 'success',
          category: 'system',
          message: 'Boxscore sync started successfully. The sync is running in the background.',
          details: {
            action: 'boxscore_sync',
            status: 'started',
            timestamp: new Date().toISOString()
          }
        })
      }
    })
    .catch(error => {
      console.error('Boxscore sync error:', error)
      
      // Show error notification
      notificationStore.addNotification({
        type: 'error',
        category: 'system',
        message: error.response?.data?.message || 'Failed to start boxscore sync. Please try again.',
        details: {
          action: 'boxscore_sync',
          status: 'error',
          error: error.message,
          timestamp: new Date().toISOString()
        }
      })
    })
    .finally(() => {
      isSyncing.value = false
    })
}
</script>

<template>
  <div class="p-6">
    <div class="space-y-6">
      <!-- Header -->
      <div>
        <h3 class="text-lg font-medium mb-2">Boxscores Management</h3>
        <p class="text-sm text-gray-500">Manage and sync boxscore data</p>
      </div>

      <!-- Sync Boxscores Section -->
      <div class="border rounded-lg p-6 bg-gray-50">
        <div class="flex items-center justify-between">
          <div>
            <h4 class="font-medium text-gray-900 mb-1">Sync Boxscores</h4>
            <p class="text-sm text-gray-600">
              Manually trigger a boxscore synchronization. The sync will run in the background.
            </p>
          </div>
          <button
            @click="openConfirmModal"
            v-if="currentUser.role === 'admin'"
            :disabled="isSyncing"
            class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Sync Boxscores"
          >
            <i v-if="isSyncing" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-sync-alt"></i>
            <span>{{ isSyncing ? 'Syncing...' : 'Sync Boxscores' }}</span>
          </button>
        </div>
      </div>

      <!-- Info Section -->
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex items-start gap-2 text-blue-800">
          <i class="fas fa-info-circle mt-1"></i>
          <div>
            <div class="font-medium text-sm">About Boxscore Sync</div>
            <p class="text-xs text-blue-700 mt-1">
              The boxscore sync process retrieves and processes boxscore data from external sources. 
              This operation runs in the background and may take several minutes to complete.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Confirmation Modal Overlay -->
  <div 
    v-if="showConfirmModal"
    class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto"
    @click.self="closeConfirmModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md m-8">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
            <i class="fas fa-exclamation-triangle text-yellow-600 text-xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Confirm Boxscore Sync</h3>
            <p class="text-sm text-gray-600 mt-1">Are you sure you want to sync boxscores?</p>
          </div>
        </div>
        <button 
          @click="closeConfirmModal"
          class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <i class="fas fa-times text-gray-400"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="p-6">
        <div class="space-y-4">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-start gap-2 text-blue-800">
              <i class="fas fa-info-circle mt-1"></i>
              <div>
                <div class="font-medium text-sm mb-1">What will happen:</div>
                <ul class="text-xs text-blue-700 space-y-1 list-disc list-inside">
                  <li>The boxscore sync process will start in the background</li>
                  <li>This operation may take several minutes to complete</li>
                  <li>You will receive a notification when the sync completes</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div class="flex items-start gap-2 text-yellow-800">
              <i class="fas fa-exclamation-triangle mt-1"></i>
              <div>
                <div class="font-medium text-sm">Note:</div>
                <p class="text-xs text-yellow-700 mt-1">
                  This action cannot be undone. The sync will process all available boxscore data.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
        <button 
          @click="closeConfirmModal"
          class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button 
          @click="triggerBoxscoreSync"
          class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors flex items-center gap-2"
        >
          <i class="fas fa-sync-alt"></i>
          Yes, Sync Boxscores
        </button>
      </div>
    </div>
  </div>
</template>

